/**
 * AssetVizCopy component for automatically grabbing screenshots of asset visualization.
 * This is a decoupled version that doesn't depend on details page contexts.
 */

import {
  AssetViz,
  AssetVizController,
  AssetVizMode,
  AssetVizSceneConfig,
} from 'features/assetViz'
import { useColorScale } from 'features/dataLayers'
import { ASSET_VIZ_DEFAULT_CONFIG } from 'pages/reporting/shared/constants'
import React, { memo, useCallback, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentShape,
  GeckoFeature,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  GeckoMeasurementRange,
} from 'types'

import { LegendCopy } from './LegendCopy'

const LegendContainer = styled.div`
  position: absolute;
  right: 150px;
  top: calc(50% - 180px);
`

export interface AssetVizCopyProps {
  assetVizRef: React.RefObject<AssetVizController>
  components?: GeckoComponent[]
  componentDataFrames?: GeckoComponentDataFrame[]
  markupLayers?: GeckoMarkupLayer[]
  assetModel?: GeckoAssetModel
  getColorThresholdForComponent?: (
    cdf: GeckoComponentDataFrame
  ) => GeckoColorScaleThreshold | undefined
  customMarkupLayers?: string[] // Array of markup layer IDs to filter
  sceneConfig?: AssetVizSceneConfig
  mode?: AssetVizMode
  showLegend?: boolean
  offscreenRender?: boolean
  onSelection?: (
    selection:
      | GeckoComponentShape
      | GeckoMarkupLayer
      | GeckoMarkupPrimitive
      | GeckoFeature
      | undefined
  ) => void
}

const AssetVizCopyComponent: React.FC<AssetVizCopyProps> = ({
  assetVizRef,
  components = [],
  componentDataFrames,
  markupLayers = [],
  assetModel,
  getColorThresholdForComponent,
  customMarkupLayers = [],
  sceneConfig,
  mode = AssetVizMode.Screenshot,
  showLegend = false,
  offscreenRender = true,
  onSelection,
}) => {
  // Local state for selection and hover
  const [selection, setSelection] = useState<
    | GeckoComponentShape
    | GeckoMarkupLayer
    | GeckoMarkupPrimitive
    | GeckoFeature
    | undefined
  >()
  const [, setHoveredCoords] = useState<GeckoComponentCoords | undefined>()
  const [, setHoveredFeature] = useState<unknown>()
  const [, setDraftSelection] = useState<unknown>()

  const finalSceneConfig = sceneConfig || ASSET_VIZ_DEFAULT_CONFIG

  const primaryCdf = componentDataFrames?.[0]
  const colorThreshold = React.useMemo(() => {
    if (!primaryCdf || !getColorThresholdForComponent) return undefined
    return getColorThresholdForComponent(primaryCdf)
  }, [primaryCdf, getColorThresholdForComponent])

  const { colorScale, colorScaleMin, colorScaleMax } = useColorScale(
    primaryCdf,
    colorThreshold
  )

  const valueRange = React.useMemo(() => {
    if (
      !primaryCdf ||
      colorScaleMin === undefined ||
      colorScaleMax === undefined
    )
      return undefined
    return GeckoMeasurementRange.CreateFromValues(
      colorScaleMin,
      colorScaleMax,
      primaryCdf.data.params.measurement_unit
    )
  }, [primaryCdf, colorScaleMin, colorScaleMax])

  // Filter markup layers if custom layers are specified
  const markupLayersToShow = React.useMemo(() => {
    if (customMarkupLayers.length === 0) {
      return markupLayers
    }
    return markupLayers.filter((ml) => customMarkupLayers.includes(ml.id))
  }, [markupLayers, customMarkupLayers])

  // Handle selection change
  const handleSelection = useCallback(
    (
      newSelection:
        | GeckoComponentShape
        | GeckoMarkupLayer
        | GeckoMarkupPrimitive
        | GeckoFeature
        | undefined
    ) => {
      setSelection(newSelection)
      onSelection?.(newSelection)
    },
    [onSelection]
  )

  // Handle hover events
  const handleHover = useCallback(
    (
      tooltipFrozen: boolean,
      event?: {
        coords?: GeckoComponentCoords
        shellCourse?: unknown
        waterwall?: unknown
      }
    ) => {
      setHoveredCoords((prev: GeckoComponentCoords | undefined) => {
        if (GeckoComponentCoords.Equals(prev, event?.coords)) return prev
        return event?.coords
      })
      setHoveredFeature((prev: unknown) => {
        const newVal = event?.shellCourse ?? event?.waterwall
        if (prev === newVal) return prev
        return newVal
      })
    },
    []
  )

  // Simplified write handler - just reject since we don't have rolodex edit in this context
  const onWrite = useCallback(
    (/* selection */): Promise<void> => {
      // In the reporting context, we don't support editing
      return Promise.reject(new Error('Editing not supported in this context'))
    },
    []
  )

  // Placeholder functions for markup layer operations
  const refreshAndSelectLayer = useCallback(
    async (markupLayer: GeckoMarkupLayer): Promise<void> => {
      // In reporting context, just update selection
      setSelection(markupLayer)
    },
    []
  )

  const deletePrimitive = useCallback(
    async (/* primitive, markupLayer */): Promise<void> => {
      // In reporting context, deletion is not supported
      return Promise.reject(new Error('Deletion not supported in this context'))
    },
    []
  )

  // TODO: Maybe conditionally render AssetViz only when taking screenshots
  return (
    <>
      <div
        style={{
          height: '800px',
          width: '2000px',
          position: 'fixed',
          top: '-1000px',
          left: 0,
        }}>
        <AssetViz
          ref={assetVizRef}
          // Rolodex IDs
          components={components}
          componentDataFrames={componentDataFrames}
          markupLayers={markupLayersToShow}
          assetModel={assetModel}
          getColorThresholdForComponent={getColorThresholdForComponent}
          // Display
          sceneConfig={finalSceneConfig}
          cameraFitMultiplier={1}
          // User interaction
          mode={mode}
          selection={selection}
          cdfToFilteredValueRange={undefined}
          onSelected={handleSelection}
          onDraftSelected={setDraftSelection}
          onHovered={handleHover}
          onWrite={onWrite}
          refreshAndSelectLayer={refreshAndSelectLayer}
          deletePrimitive={deletePrimitive}
          offscreenRender={offscreenRender}>
          {/* Legend can be added here if needed */}
          {showLegend && primaryCdf ? (
            <LegendContainer>
              <LegendCopy
                cdf={primaryCdf}
                sceneConfig={finalSceneConfig}
                colorScale={colorScale}
                valueRange={valueRange}
              />
            </LegendContainer>
          ) : null}
        </AssetViz>
      </div>
    </>
  )
}

// Memoize the component to prevent unnecessary re-renders
export const AssetVizCopy = memo(AssetVizCopyComponent)
